import { memo } from 'react';
import PropTypes from 'prop-types';

/**
 * Section wrapper for drawer body content. Renders an optional
 * uppercase section heading **outside** the card; children typically wrap a
 * `DrawerPanel` or other blocks.
 *
 * @param {string} [props.title]  When omitted, only `children` render (no header row).
 * @param {import('react').ReactNode} props.children
 * @param {import('react').ReactNode} [props.rightContent]  Aligned to the right of the title row.
 * @param {string} [props.className]  Root element.
 * @param {string} [props.titleClassName]  Title span only.
 * @param {string} [props.contentClassName]  Wrapper around `children`.
 */
const DrawerSection = ({
  title,
  children,
  rightContent,
  className = '',
  titleClassName = '',
  contentClassName = '',
}) => {
  const hasTitle = Boolean(title && String(title).trim());

  return (
    <section
      className={['space-y-3', className].filter(Boolean).join(' ')}
    >
      {hasTitle ? (
        <div className="flex items-start justify-between gap-3">
          <h3
            className={[
              'text-xs font-semibold uppercase tracking-widest text-content-faint',
              titleClassName,
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {title}
          </h3>
          {rightContent ? (
            <div className="shrink-0">{rightContent}</div>
          ) : null}
        </div>
      ) : rightContent ? (
        <div className="flex justify-end">{rightContent}</div>
      ) : null}

      <div className={['min-w-0', contentClassName].filter(Boolean).join(' ')}>{children}</div>
    </section>
  );
};

DrawerSection.propTypes = {
  title: PropTypes.string,
  children: PropTypes.node,
  rightContent: PropTypes.node,
  className: PropTypes.string,
  titleClassName: PropTypes.string,
  contentClassName: PropTypes.string,
};

DrawerSection.displayName = 'DrawerSection';

export default memo(DrawerSection);
